import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { ME_API, UPDATE_PROFILE_API } from "../../apis/apis";
import { getErr } from "../../components/portalUtils";
import { ErrorBox, Loader } from "../../components/PortalBits";

const FIELDS = [
  { key: "name", label: "Full name" },
  { key: "phone", label: "Phone", type: "tel" },
  { key: "organization", label: "Organization" },
  { key: "designation", label: "Designation" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "country", label: "Country" },
  { key: "region", label: "Region" },
];

const clean = {
  phone: (v) => String(v || "").replace(/\D/g, "").slice(0, 10),
};

function AccountDetails() {
  const { refreshUser } = useOutletContext() || {};
  const [values, setValues] = useState({});
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await ME_API();
      setEmail(data?.email || "");
      const v = {};
      FIELDS.forEach((f) => {
        v[f.key] = f.key === "phone" ? clean.phone(data?.[f.key]) : data?.[f.key] || "";
      });
      setValues(v);
    } catch (err) {
      setLoadError(getErr(err, "Unable to load account details."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const validate = () => {
    const e = {};
    if (!values.name?.trim()) e.name = "Name is required.";
    else if (!/^[A-Za-z][A-Za-z .'-]{1,99}$/.test(values.name.trim())) e.name = "Use letters, spaces, dots, apostrophes and hyphens only.";
    if (values.phone && !/^\d{10}$/.test(values.phone)) e.phone = "Enter a valid 10-digit mobile number.";
    return e;
  };

  const save = async () => {
    setFormError(null);
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      // Email is fixed to the account (identity) and is never sent for update.
      const payload = {};
      FIELDS.forEach((f) => {
        payload[f.key] = String(values[f.key] ?? "").trim();
      });
      const res = await UPDATE_PROFILE_API(payload);
      if (res?.status === false) {
        setErrors(res?.errors || {});
        setFormError(res?.message || "Validation failed.");
        return;
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (refreshUser) refreshUser();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) setErrors(data.errors);
      setFormError(getErr(err, "Failed to update account details."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="panel">
      <h2>Account details</h2>
      <div className="sub">Keep your contact and organization details up to date. Your email is fixed to your account.</div>
      {loading && <Loader />}
      {!loading && loadError && <ErrorBox message={loadError} onRetry={load} />}
      {!loading && !loadError && (
        <>
          <div className="cert-form">
            <div className="field">
              <label htmlFor="acc-email">Email</label>
              <input id="acc-email" type="email" value={email} readOnly />
            </div>
            {FIELDS.map((f) => (
              <div key={f.key} className="field">
                <label htmlFor={`acc-${f.key}`}>{f.label}</label>
                <input
                  id={`acc-${f.key}`}
                  type={f.type || "text"}
                  maxLength={f.key === "phone" ? 10 : 255}
                  value={values[f.key] || ""}
                  className={errors[f.key] ? "invalid" : ""}
                  onChange={(e) => {
                    const v = clean[f.key] ? clean[f.key](e.target.value) : e.target.value;
                    setValues((s) => ({ ...s, [f.key]: v }));
                  }}
                />
                {errors[f.key] && <div className="field-error">{Array.isArray(errors[f.key]) ? errors[f.key].join(", ") : errors[f.key]}</div>}
              </div>
            ))}
          </div>
          {formError && <div className="alert error">{formError}</div>}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>
              {saving ? "Saving…" : "Save changes"}
            </button>
            <span style={{ fontSize: 12.5, color: "var(--success)", fontWeight: 600, opacity: saved ? 1 : 0, transition: "opacity .2s" }}>
              ✓ Changes saved
            </span>
          </div>
        </>
      )}
    </div>
  );
}

export default AccountDetails;
